<div class="flex flex-col flex-1 min-h-0 w-full overflow-hidden">
  <!-- Header section (not scrollable) -->
  <div class="flex flex-col gap-3 p-3 flex-shrink-0">
    <div class="flex justify-between items-center">
      <span class="font-light text-md text-[#ADADAD]">Manage {{ permissionType | titlecase }} Permissions for {{ collectionName }}</span>

      <div class="flex items-center gap-4">
        <nrc-search
          [placeholder]="'Search...'"
          [isCollapsible]="true"
          [isCompact]="true"
          [debounceTime]="300"
          (searchEvent)="handleSearch($event)"
        />

        <nrc-button
          class="w-6 h-6 flex justify-center items-center relative mr-4"
          [icon]="permissionType === 'READ' ? 'preview' : permissionType === 'UPDATE' ? 'pencil' : permissionType === 'DELETE' ? 'trash' : permissionType === 'CREATE' ? 'plus' : 'rocket'"
          [role]="overallState() === 'ALLOW' ? actionRole.SUCCESS : overallState() === 'DENY' ? actionRole.ERROR : actionRole.DISABLED"
          [buttonType]="buttonType.ICON"
          [isActive]="true"
          [isRound]="false"
          (clickEvent)="toggleAllAccess()"
        />
      </div>
    </div>
  </div>

  <!-- Scrollable list area -->
  <div
    class="flex-1 overflow-y-auto px-3"
    nrcInfiniteScroll
    [threshold]="infiniteScrollConfig.threshold || 200"
    [debounceTime]="infiniteScrollConfig.debounceTime || 100"
    [isInfiniteScrollDisabled]="!canLoadMore()"
    (scrolledToBottom)="handleScrolledToBottom()"
  >
    @if (isGroupingEnabled()) {
      <!-- Grouped view -->
      @for (group of groupedItems(); track groupIndex; let groupIndex = $index) {
        <!-- Group header -->
        <div
          class="flex w-full justify-between items-center bg-white/10 backdrop-blur-sm border-b border-solid border-white/10 h-12 py-1 pr-4 sticky z-10"
          [style.top.px]="groupIndex * 48"
        >
          <div
            class="flex items-center gap-2 flex-1 cursor-pointer"
            tabindex="0"
            role="button"
            (click)="config.groupingConfig?.collapsible !== false && toggleGroupCollapse(group.groupKey)"
            (keydown.enter)="config.groupingConfig?.collapsible !== false && toggleGroupCollapse(group.groupKey)"
          >
            @if (config.groupingConfig?.collapsible !== false) {
              <nrc-icon
                class="w-4 h-4 ml-2 text-[#ADADAD] transition-transform duration-250"
                [ngClass]="{
                  '-rotate-90': group.isCollapsed,
                  'rotate-0': !group.isCollapsed
                }"
                [name]="'arrow-down'"
              />
            }
            <span class="font-medium text-sm text-white">{{ group.groupLabel }}</span>
            <span class="text-xs text-[#ADADAD] bg-white/10 px-2 py-0.5 rounded-full">{{ group.items.length }}</span>
          </div>
          <div class="flex justify-center items-center">
            <nrc-button
              class="w-6 h-6 flex justify-center items-center relative"
              [icon]="getGroupState(group) === 'ALLOW' ? 'full-access' : getGroupState(group) === 'DENY' ? 'no-access' : 'custom-access'"
              [buttonType]="buttonType.ICON"
              [role]="getGroupState(group) === 'ALLOW' ? actionRole.SUCCESS : getGroupState(group) === 'DENY' ? actionRole.ERROR : actionRole.WARNING"
              [isActive]="true"
              [isRound]="false"
              (clickEvent)="toggleGroupAccess(group)"
            />
          </div>
        </div>

        <!-- Group items -->
        @if (!group.isCollapsed) {
          @for (item of group.items; track itemIndex; let itemIndex = $index) {
            <div class="group flex w-full justify-between hover:bg-white/5 border-b border-solid border-white/10 cursor-pointer last:border-b-0 h-12 py-1 pr-4 pl-6">
              @for (column of config.columns; track colIndex; let colIndex = $index) {
                <div class="flex flex-1" tabindex="0" (click)="rowClickedEvent.emit({ index: itemIndex, item })" (keydown)="null">
                  <nrc-collection-permission-list-item
                    class="flex w-full min-w-60 justify-center items-center"
                    [ngClass]="{
                      'justify-start': !column.align || column.align === 'left',
                      'justify-center mr-13': column.align === 'center',
                      'justify-end': column.align === 'right'
                    }"
                    [index]="itemIndex"
                    [isSelected]="item.isSelected"
                    [config]="{
                      type: column.type,
                      value: item.value | extractProperty: column.key,
                      additionalInfo: column['additionalInfoKey'] ? (item.value | extractProperty: (column['additionalInfoKey'] | toString)) : undefined,
                      thumbnailConfig: column.thumbnailConfig,
                      mimetype: column.thumbnailConfig && column.thumbnailConfig.thumbnailSourceKey && (item.value | extractProperty: (column.thumbnailConfig.thumbnailSourceKey | toString) | toString)
                        ? 'image/png'
                        : (item.value | extractProperty: (column.thumbnailConfig?.fallbackMimetypeKey | toString) | toString),
                      thumbnailSource: column.thumbnailConfig && column.thumbnailConfig.thumbnailSourceKey
                        ? (item.value | extractProperty: (column.thumbnailConfig.thumbnailSourceKey | toString) | toString)
                          ? (item.value | extractProperty: (column.thumbnailConfig.thumbnailSourceKey | toString) | toString)
                          : (item.value | extractProperty: (column.thumbnailConfig.fallbackThumbnailSourceKey | toString) | toString)
                        : undefined,
                      profileColor: item.value['profileColor'],
                      isHighlighted: column.isHighlighted,
                      actionRole: item.actionRole,
                      tooltipText: column['tooltipText'],
                      extractorFn: column['extractorFn'],
                      tooltipTextFn: column['tooltipTextFn'],
                      visibilityFn: column['visibilityFn']
                    }"
                    (rowListItemClickedEvent)="rowClickedEvent.emit({ index: itemIndex, itemIndex: colIndex, item })"
                  />
                </div>
              }
              <div class="flex justify-center items-center">
                <nrc-button
                  class="w-6 h-6 flex justify-center items-center relative"
                  [icon]="item.value.access === 'ALLOW' ? 'full-access' : 'no-access'"
                  [buttonType]="buttonType.ICON"
                  [role]="actionRole.TRANSPARENT_SECONDARY"
                  [isActive]="true"
                  [isRound]="false"
                  (clickEvent)="toggleAccess(item.value)"
                />
              </div>
            </div>
          }
        }
      }
    } @else {
      <!-- Flat view (original) -->
      @for (item of items(); track rowIndex; let rowIndex = $index) {
        <div class="group flex w-full justify-between hover:bg-white/5 border-b border-solid border-white/10 cursor-pointer last:border-b-0 h-12 py-1 pr-4">
          @for (column of config.columns; track itemIndex; let itemIndex = $index) {
            <div class="flex flex-1" tabindex="0" (click)="rowClickedEvent.emit({ index: rowIndex, item })" (keydown)="null">
              <nrc-collection-permission-list-item
                class="flex w-full min-w-60 justify-center items-center"
                [ngClass]="{
                  'justify-start': !column.align || column.align === 'left',
                  'justify-center mr-13': column.align === 'center',
                  'justify-end': column.align === 'right'
                }"
                [index]="rowIndex"
                [isSelected]="item.isSelected"
                [config]="{
                  type: column.type,
                  value: item.value | extractProperty: column.key,
                  additionalInfo: column['additionalInfoKey'] ? (item.value | extractProperty: (column['additionalInfoKey'] | toString)) : undefined,
                  thumbnailConfig: column.thumbnailConfig,
                  mimetype: column.thumbnailConfig && column.thumbnailConfig.thumbnailSourceKey && (item.value | extractProperty: (column.thumbnailConfig.thumbnailSourceKey | toString) | toString)
                    ? 'image/png'
                    : (item.value | extractProperty: (column.thumbnailConfig?.fallbackMimetypeKey | toString) | toString),
                  thumbnailSource: column.thumbnailConfig && column.thumbnailConfig.thumbnailSourceKey
                    ? (item.value | extractProperty: (column.thumbnailConfig.thumbnailSourceKey | toString) | toString)
                      ? (item.value | extractProperty: (column.thumbnailConfig.thumbnailSourceKey | toString) | toString)
                      : (item.value | extractProperty: (column.thumbnailConfig.fallbackThumbnailSourceKey | toString) | toString)
                    : undefined,
                  profileColor: item.value['profileColor'],
                  isHighlighted: column.isHighlighted,
                  actionRole: item.actionRole,
                  tooltipText: column['tooltipText'],
                  extractorFn: column['extractorFn'],
                  tooltipTextFn: column['tooltipTextFn'],
                  visibilityFn: column['visibilityFn']
                }"
                (rowListItemClickedEvent)="rowClickedEvent.emit({ index: rowIndex, itemIndex, item })"
              />
            </div>
          }
          <div class="flex justify-center items-center">
            <nrc-button
              class="w-6 h-6 flex justify-center items-center relative"
              [icon]="item.value.access === 'ALLOW' ? 'full-access' : 'no-access'"
              [buttonType]="buttonType.ICON"
              [role]="actionRole.TRANSPARENT_SECONDARY"
              [isActive]="true"
              [isRound]="false"
              (clickEvent)="toggleAccess(item.value)"
            />
          </div>
        </div>
      }
    }

    <!-- Loading More Indicator -->
    @if (isLoadingMore) {
      <div class="flex justify-center items-center py-4">
        <span class="text-sm text-gray-400">Loading more items...</span>
      </div>
    }

    <!-- End of List Indicator -->
    @if (infiniteScrollConfig.enabled && !hasMoreData && items()?.length && !isLoadingMore) {
      <div class="flex justify-center items-center py-4">
        <span class="text-sm text-gray-400">No more items to load</span>
      </div>
    }
  </div>
</div>
